/**
 * The cathedral, as reflections only: a dark box with three tall lancet windows, baked to
 * a PMREM. Tubes pick up long vertical highlights from it; nothing architectural is drawn.
 * Also the four tube finishes and the glow colour each one runs when struck.
 */
import {
  BoxGeometry, Color, Mesh, MeshBasicMaterial, MeshPhysicalMaterial, MeshStandardMaterial,
  PMREMGenerator, Scene, Shape, ShapeGeometry, BackSide, DoubleSide,
  type Texture, type WebGLRenderer,
} from 'three';
import { PALETTE } from '../config';
import type { MaterialName } from '../types';

/** a pointed (lancet) arch outline in the XY plane, base at y = 0 */
export function lancetShape(w: number, h: number): Shape {
  const s = new Shape();
  const shoulder = h - w * 1.35;
  s.moveTo(-w / 2, 0);
  s.lineTo(w / 2, 0);
  s.lineTo(w / 2, shoulder);
  s.quadraticCurveTo(w / 2, h - w * 0.25, 0, h);
  s.quadraticCurveTo(-w / 2, h - w * 0.25, -w / 2, shoulder);
  s.closePath();
  return s;
}

function emissive(hex: string, k: number): MeshBasicMaterial {
  const c = new Color(hex);
  c.multiplyScalar(k);
  return new MeshBasicMaterial({ color: c, side: DoubleSide, toneMapped: false });
}

/**
 * Bake the environment. Windows are placed around the origin (which is where the PMREM
 * cube is rendered from) at the azimuths that reflect toward a viewer on +Z.
 */
export function buildEnvironment(renderer: WebGLRenderer): Texture {
  const scene = new Scene();
  const room = new Mesh(
    new BoxGeometry(14, 16, 14),
    new MeshBasicMaterial({ color: new Color('#07060a'), side: BackSide, toneMapped: false }),
  );
  scene.add(room);

  // a faint ember floor: the red half of the flag, felt from below
  const floor = new Mesh(new BoxGeometry(14, 0.02, 14), emissive('#2a0d12', 0.45));
  floor.position.y = -7.9;
  scene.add(floor);

  const windows: { az: number; y: number; w: number; h: number; hex: string; k: number }[] = [
    { az: -58, y: -1.6, w: 0.9, h: 6.5, hex: '#ffd4a0', k: 4.2 }, // warm key, upper left
    { az: 44, y: -1.2, w: 0.8, h: 5.8, hex: '#a9c2ff', k: 2.4 }, // cool, right
    { az: 168, y: -2.4, w: 1.1, h: 6.0, hex: '#ffb890', k: 1.4 }, // warm, behind the chimes
  ];
  const R = 6.2;
  for (const win of windows) {
    const a = (win.az * Math.PI) / 180;
    const x = Math.sin(a) * R, z = Math.cos(a) * R;
    const geo = new ShapeGeometry(lancetShape(win.w, win.h), 12);
    const m = new Mesh(geo, emissive(win.hex, win.k));
    m.position.set(x, win.y, z);
    m.lookAt(0, win.y, 0);
    scene.add(m);
    // a soft halo behind each window so the highlight has a body, not just an edge
    const halo = new Mesh(new ShapeGeometry(lancetShape(win.w * 2.6, win.h * 1.25), 12), emissive(win.hex, win.k * 0.08));
    halo.position.set(x * 1.03, win.y - win.h * 0.12, z * 1.03);
    halo.lookAt(0, win.y, 0);
    scene.add(halo);
  }

  // the red rim, lower right: a low, wide, dim ember
  const rim = new Mesh(new ShapeGeometry(lancetShape(4, 3), 8), emissive(PALETTE.redHot, 0.5));
  rim.position.set(4.5, -6.5, 3.2);
  rim.lookAt(0, -3, 0);
  scene.add(rim);

  const pmrem = new PMREMGenerator(renderer);
  const rt = pmrem.fromScene(scene, 0.04, 0.1, 60);
  pmrem.dispose();
  scene.traverse((o) => {
    const mesh = o as Mesh;
    if (mesh.isMesh) { mesh.geometry.dispose(); (mesh.material as MeshBasicMaterial).dispose(); }
  });
  return rt.texture;
}

export interface Finish {
  make(): MeshPhysicalMaterial;
  /** emissive colour when struck */
  glow: Color;
  /** emissiveIntensity at energy 1 (scene.ts applies the energy curve) */
  glowK: number;
  /** sprite glow opacity at energy 1 */
  haloK: number;
}

/**
 * Struck metal, not a glow stick. Two shapes on the emissive term: it is strongest where
 * the surface faces the viewer and dies toward the silhouette, so the cylinder keeps its
 * roundness and rim reflections under the glow; and it is uneven along the tube, warmer
 * patches creeping slowly down the length (`time` is the caller's clock; hold it still
 * under reduced motion). Injected after the emissive chunk, where `normal` (view space)
 * and vViewPosition are in scope; the tube's local Y (a unit cylinder, -0.5..0.5) rides a
 * varying from the vertex stage.
 */
export function shapeEmber(m: MeshPhysicalMaterial, time: { value: number }, phase: number): void {
  m.onBeforeCompile = (shader) => {
    shader.uniforms.uEmberT = time;
    shader.uniforms.uEmberPhase = { value: phase };
    shader.vertexShader = 'varying float vTubeY;\n' + shader.vertexShader.replace(
      '#include <begin_vertex>',
      '#include <begin_vertex>\n vTubeY = position.y;',
    );
    shader.fragmentShader = 'uniform float uEmberT;\nuniform float uEmberPhase;\nvarying float vTubeY;\n' + shader.fragmentShader.replace(
      '#include <emissivemap_fragment>',
      `#include <emissivemap_fragment>
      {
        float facing = abs(dot(normalize(normal), normalize(vViewPosition)));
        float creep = 0.68 + 0.32 * sin(vTubeY * 11.0 - uEmberT * 0.9 + uEmberPhase)
                           * (0.6 + 0.4 * sin(vTubeY * 27.0 + uEmberT * 0.35 + uEmberPhase * 2.0));
        totalEmissiveRadiance *= mix(0.18, 1.0, pow(facing, 1.6)) * creep;
      }`,
    );
  };
  m.customProgramCacheKey = () => 'ember';
}

export const FINISHES: Record<MaterialName, Finish> = {
  iron: {
    make: () => new MeshPhysicalMaterial({
      color: new Color(PALETTE.iron), metalness: 0.92, roughness: 0.34, envMapIntensity: 1.1,
      emissive: new Color('#ff5a1e'), emissiveIntensity: 0,
    }),
    glow: new Color('#ff6a2a'), glowK: 0.11, haloK: 0.1,
  },
  oxblood: {
    make: () => new MeshPhysicalMaterial({
      color: new Color(PALETTE.red), metalness: 0.85, roughness: 0.28, envMapIntensity: 1.0,
      clearcoat: 1, clearcoatRoughness: 0.14,
      emissive: new Color(PALETTE.redHot), emissiveIntensity: 0,
    }),
    glow: new Color('#ff3446'), glowK: 0.15, haloK: 0.11,
  },
  bone: {
    make: () => new MeshPhysicalMaterial({
      color: new Color(PALETTE.bone), metalness: 0.1, roughness: 0.45, envMapIntensity: 0.9,
      clearcoat: 0.7, clearcoatRoughness: 0.28,
      emissive: new Color(PALETTE.gold), emissiveIntensity: 0,
    }),
    glow: new Color('#e8b44a'), glowK: 0.12, haloK: 0.08,
  },
  brass: {
    make: () => new MeshPhysicalMaterial({
      color: new Color(PALETTE.brass), metalness: 1, roughness: 0.3, envMapIntensity: 1.1,
      emissive: new Color('#fff0c8'), emissiveIntensity: 0,
    }),
    glow: new Color('#ffe9b8'), glowK: 0.13, haloK: 0.09,
  },
};

export function ironWire(): MeshStandardMaterial {
  return new MeshStandardMaterial({ color: new Color('#1c1b1f'), metalness: 0.7, roughness: 0.55, envMapIntensity: 0.8 });
}
